// AdminUsers.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adminFetchUsers,
  adminUpdateUserRole,
  adminUpdateUserPremium,
  ADMIN_ALLOWED_ROLES,
  type AdminUser,
  type AdminProfile,
  adminFetchUserProfiles,
  adminFetchUserLegal,
  type PlanType,
} from '../../../../api/admin/users';

import '../../../../assets/styles/users.scss';

/* ========================================
 * Stałe i typy
 * ====================================== */
const pageSize = 20;
type PremiumAction = 'grant' | 'extend' | 'revoke';

type PendingAction = {
  userId: string;
  action: PremiumAction;
  plan_type?: PlanType;
};

const PLAN_PROFILES: Record<PlanType, 1 | 2 | 4> = {
  BASIC: 1,
  STANDARD: 2,
  FAMILY: 4,
};

/* Widoczność kolumn */
type ColumnKey =
  | 'email'
  | 'role'
  | 'verified'
  | 'legal'
  | 'notifications'
  | 'premium'
  | 'expires'
  | 'last_active'
  | 'created'
  | 'actions';

const COLUMN_LABELS: Record<ColumnKey, string> = {
  email: 'Email',
  role: 'Rola',
  verified: 'Zweryfikowany',
  legal: 'Regulamin/PP',
  notifications: 'Powiadomienia',
  premium: 'Premium',
  expires: 'Wygasa',
  last_active: 'Ostatnia aktywność',
  created: 'Utworzono',
  actions: 'Akcje',
};

/* Domyślnie widoczne (oprócz ID) */
const DEFAULT_VISIBLE_COLS: Record<ColumnKey, boolean> = {
  email: true,
  role: true,
  notifications: true,
  premium: true,
  actions: false,
  verified: false,
  legal: false,
  expires: false,
  last_active: false,
  created: false,
};

/* ========================================
 * Mapy pomocnicze dla metadanych profili
 * ====================================== */

// poziomy – spójne z mobile
const LEVEL_LABELS: Record<number, string> = {
  1: 'Łatwy (A1–A2)',
  2: 'Średni (B1–B2)',
  3: 'Trudny (C1+)',
};

const LEARNING_GOAL_LABELS: Record<string, string> = {
  work: 'Praca i kariera',
  travel: 'Podróże',
  study: 'Szkoła / studia',
  move_abroad: 'Życie za granicą',
  fun: 'Hobby i rozrywka',
  speaking: 'Mówienie / swobodna komunikacja',
};

const INTEREST_CATEGORY_LABELS: Record<number, string> = {
  3: 'Podróże',
  4: 'Natura',
  5: 'Dla początkujących',
  7: 'Styl życia',
  8: 'Praca i kariera',
  11: 'Jedzenie i gotowanie',
  12: 'Przydatne zwroty',
  16: 'Rodzina i relacje',
  17: 'Sport i aktywność',
  19: 'Emocje i samopoczucie',
};

function formatInterests(ids?: number[]): string {
  if (!ids || ids.length === 0) return 'brak';
  const labels = ids.map(id => INTEREST_CATEGORY_LABELS[id] || `ID ${id}`);
  return labels.join(', ');
}

function formatGoals(goals?: string[] | null): string {
  if (!goals || goals.length === 0) return 'brak';
  const labels = goals.map(g => LEARNING_GOAL_LABELS[g] || g);
  return labels.join(', ');
}

/* ========================================
 * Helpery (bez stanu)
 * ====================================== */
function getRoleFromToken(): { role?: string; email?: string } {
  try {
    const t = localStorage.getItem('token');
    if (!t) return {};
    const [, payloadB64] = t.split('.');
    if (!payloadB64) return {};
    const json = JSON.parse(atob(payloadB64));
    const role = (json.role || json.user?.role || json.claims?.role || '').toString();
    const email = (json.email || json.user?.email || json.sub || '').toString();
    return { role, email };
  } catch {
    return {};
  }
}

function getProfilesCount(u: AdminUser): 1 | 2 | 4 | undefined {
  const sub = (u as any).subscription;
  if (!sub) return undefined;
  if (typeof sub.max_profiles === 'number' && [1, 2, 4].includes(Number(sub.max_profiles))) {
    return Number(sub.max_profiles) as 1 | 2 | 4;
  }
  const plan = (sub.plan_type || '') as PlanType;
  return plan && PLAN_PROFILES[plan] ? PLAN_PROFILES[plan] : undefined;
}

/** Czy subskrypcja realnie aktywna w tym momencie */
function isSubscriptionActive(u: AdminUser): boolean {
  const sub = (u as any).subscription;
  if (!sub) return false;
  if (sub.is_active === false) return false;
  if (sub.expires_at && new Date(sub.expires_at).getTime() <= Date.now()) return false;
  return !!sub.is_active || !sub.expires_at; // tolerujemy brak expires_at przy is_active
}

/** Ile profili dozwolone teraz (1 jeśli brak aktywnego premium) */
function allowedProfilesNow(u: AdminUser): 1 | 2 | 4 {
  if (isSubscriptionActive(u)) {
    return getProfilesCount(u) || 1;
  }
  return 1;
}

/** sortowanie profili: najpierw main, potem po id rosnąco */
function sortProfiles(list: AdminProfile[]): AdminProfile[] {
  return [...list].sort((a, b) => {
    if (a.is_main && !b.is_main) return -1;
    if (!a.is_main && b.is_main) return 1;
    return (a.id || 0) - (b.id || 0);
  });
}

/* Ładniejsze ikony e-mail/telefon */
function NotificationsCell({
  email,
  local,
}: {
  email?: boolean;
  local?: boolean;
}) {
  const onlyEmail = !!email && !local;
  const onlyLocal = !!local && !email;
  const both = !!email && !!local;

  if (!email && !local) {
    return <span className="badge badge-muted">—</span>;
  }

  return (
    <span
      className="notif-icons"
      title={both ? 'E-mail i lokalne' : onlyEmail ? 'Tylko e-mail' : 'Tylko lokalne'}
      aria-label={both ? 'Powiadomienia e-mail i telefon' : onlyEmail ? 'Powiadomienia e-mail' : 'Powiadomienia telefon'}
    >
      {email && (
        <span className="notif-ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <rect
              x="3"
              y="4"
              width="18"
              height="16"
              rx="3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.5 6.5 12 11.5 19.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      <span className="notif-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <rect
            x="6"
            y="2.5"
            width="12"
            height="19"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="9" y="5.25" width="6" height="1.5" rx="0.75" fill="currentColor" />
          <rect x="10" y="18.5" width="4" height="1.75" rx="0.875" fill="currentColor" />
        </svg>
      </span>
    </span>
  );
}

/* ========================================
 * Komponent
 * ====================================== */
export default function Users() {
  /* ---------- Filtry, paginacja, statusy ---------- */
  const [query, setQuery] = useState('');
  const [premiumFilter, setPremiumFilter] = useState<'any' | 'true' | 'false'>('any');
  const [roleFilter, setRoleFilter] = useState<'any' | 'admin' | 'user' | 'guest' | 'worker'>('any');
  const [inactiveGtDays, setInactiveGtDays] = useState<'any' | '10' | '30' | '60'>('any');

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [months, setMonths] = useState<number>(1);

  /* ---------- Dane użytkowników ---------- */
  const [data, setData] = useState<{ items: AdminUser[]; total: number; page: number; limit: number }>({
    items: [],
    total: 0,
    page: 1,
    limit: pageSize,
  });
  const searchTermRef = useRef<string>('');
  const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / pageSize)), [data.total]);

  /* ---------- Zmiana roli ---------- */
  const [roleConfirmOpen, setRoleConfirmOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<{
    userId: string;
    email: string;
    prevRole: string;
    nextRole: 'admin' | 'worker' | 'user' | 'guest';
  } | null>(null);
  const [roleChanging, setRoleChanging] = useState<string | null>(null);
  const [selfRole, setSelfRole] = useState<'admin' | 'worker' | 'user' | 'guest' | string>('user');

  /* ---------- Premium – potwierdzenie ---------- */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  /* ---------- Szczegóły użytkownika (lazy-load) ---------- */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [profilesCache, setProfilesCache] = useState<Record<string, AdminProfile[]>>({});
  const [legalCache, setLegalCache] = useState<Record<string, { accepted: boolean; accepted_at: string | null }>>({});
  const [loadingDetails, setLoadingDetails] = useState<string | null>(null);

  /* ---------- Widoczność kolumn ---------- */
  const [visibleCols, setVisibleCols] = useState<Record<ColumnKey, boolean>>(() => {
    try {
      const saved = localStorage.getItem('users_visibleCols');
      return saved ? { ...DEFAULT_VISIBLE_COLS, ...JSON.parse(saved) } : DEFAULT_VISIBLE_COLS;
    } catch {
      return DEFAULT_VISIBLE_COLS;
    }
  });
  useEffect(() => {
    localStorage.setItem('users_visibleCols', JSON.stringify(visibleCols));
  }, [visibleCols]);
  const [colsOpen, setColsOpen] = useState(false);
  const visibleCount = useMemo(() => Object.values(visibleCols).filter(Boolean).length, [visibleCols]);

  const colsBtnRef = useRef<HTMLButtonElement | null>(null);
  const colsPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!colsOpen) return;

    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (colsPanelRef.current?.contains(t) || colsBtnRef.current?.contains(t)) return;
      setColsOpen(false);
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setColsOpen(false);
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [colsOpen]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await adminFetchUsers({
        query: searchTermRef.current,
        page,
        limit: pageSize,
        premium: premiumFilter,
        role: roleFilter,
        inactive_gt_days: inactiveGtDays === 'any' ? undefined : Number(inactiveGtDays),
      });
      setData(res);
    } catch (e: any) {
      setError(e?.message || 'Błąd pobierania użytkowników');
    } finally {
      setLoading(false);
    }
  }, [page, premiumFilter, roleFilter, inactiveGtDays]);

  /* ========================================
   * Efekty
   * ====================================== */
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const fromJwt = getRoleFromToken().role;
    if (fromJwt) {
      setSelfRole(fromJwt);
      return;
    }
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('me') || '';
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj?.role) setSelfRole(obj.role);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user' || e.key === 'me') {
        const r = getRoleFromToken().role;
        if (r) setSelfRole(r);
        else {
          try {
            const raw = localStorage.getItem('user') || localStorage.getItem('me') || '';
            const obj = raw ? JSON.parse(raw) : null;
            setSelfRole(obj?.role || 'user');
          } catch {
            setSelfRole('user');
          }
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* ========================================
   * Handlery akcji – premium
   * ====================================== */
  function openConfirm(user: AdminUser, action: PremiumAction) {
    const sub: any = (user as any).subscription || {};
    const currentPlan = (sub.plan_type || sub.plan) as PlanType | undefined;

    // reset liczby miesięcy przy każdym otwarciu
    setMonths(1);

    setPendingAction({
      userId: user.id,
      action,
      plan_type:
        action === 'revoke'
          ? undefined
          : (currentPlan && (PLAN_PROFILES as any)[currentPlan] ? currentPlan : 'BASIC'),
    });
    setConfirmOpen(true);
  }

  function closeConfirm() {
    setConfirmOpen(false);
    setPendingAction(null);
  }

  async function confirmAction() {
    if (!pendingAction) return;
    try {
      const payload: any = { action: pendingAction.action };

      if (pendingAction.action !== 'revoke') {
        payload.months = months > 0 ? months : 1;
        if (pendingAction.plan_type) {
          payload.plan_type = pendingAction.plan_type;
        }
      }

      await adminUpdateUserPremium(pendingAction.userId, payload as any);
      await loadUsers();
    } catch (e: any) {
      alert(e?.message || 'Błąd zmiany statusu premium');
    } finally {
      closeConfirm();
    }
  }

  /* ========================================
   * Handlery akcji – zmiana roli
   * ====================================== */
  function requestRoleChange(u: AdminUser, nextRole: 'admin' | 'worker' | 'user' | 'guest') {
    if (u.role === nextRole) return;

    const meIsAdmin = (selfRole || '').toString().toLowerCase() === 'admin';
    if (!meIsAdmin) {
      alert('Brak uprawnień do zmiany roli (wymagana rola: admin).');
      loadUsers();
      return;
    }
    if (!ADMIN_ALLOWED_ROLES.includes(nextRole)) {
      alert(`Nieprawidłowa rola: ${nextRole}`);
      loadUsers();
      return;
    }
    setPendingRole({ userId: u.id, email: u.email, prevRole: u.role as any, nextRole });
    setRoleConfirmOpen(true);
  }

  function ProfilesChip({ count }: { count: 1 | 2 | 4 }) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          marginLeft: 6,
          padding: '2px 6px',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          background: '#fff',
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1,
        }}
        title={`Liczba profili: ${count}`}
      >
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        {count}
      </span>
    );
  }

  async function confirmRoleChange() {
    if (!pendingRole) return;
    try {
      setRoleChanging(pendingRole.userId);
      await adminUpdateUserRole(pendingRole.userId, pendingRole.nextRole);
      setData(prev => ({
        ...prev,
        items: prev.items.map(it => (it.id === pendingRole.userId ? { ...it, role: pendingRole.nextRole } : it)),
      }));
    } catch (e: any) {
      alert(e?.message || 'Nie udało się zmienić roli');
    } finally {
      setRoleChanging(null);
      setRoleConfirmOpen(false);
      setPendingRole(null);
    }
  }

  /* ========================================
   * Szczegóły usera (profile + prawo)
   * ====================================== */
  async function toggleExpand(u: AdminUser) {
    const id = u.id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);

    // lazy-load szczegółów
    if (!profilesCache[id] || !legalCache[id]) {
      setLoadingDetails(id);
      try {
        const [profilesRes, legalRes] = await Promise.allSettled([
          adminFetchUserProfiles(id),
          adminFetchUserLegal(id),
        ]);

        if (profilesRes.status === 'fulfilled') {
          setProfilesCache(prev => ({ ...prev, [id]: sortProfiles(profilesRes.value.items || []) }));
        }
        if (legalRes.status === 'fulfilled') {
          setLegalCache(prev => ({
            ...prev,
            [id]: {
              accepted: !!legalRes.value.accepted_latest,
              accepted_at: legalRes.value.accepted_at || null,
            },
          }));
        }
      } catch {
        /* ignore */
      } finally {
        setLoadingDetails(null);
      }
    }
  }

  /* ========================================
   * Render
   * ====================================== */
  return (
    <div className="admin-root users-violet">
      <header className="admin-header">
        <h1>Panel administratora</h1>
      </header>

      {/* Filtry */}
      <section className="admin-controls card">
        <div className="form-row">
          <div className="field">
            <label htmlFor="searchEmail">Szukaj (email)</label>
            <input
              id="searchEmail"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="np. user@domain.com"
              className="input"
            />
          </div>
          <div className="field">
            <label htmlFor="premiumFilter">Premium</label>
            <select
              id="premiumFilter"
              value={premiumFilter}
              onChange={e => setPremiumFilter(e.target.value as any)}
              className="select"
            >
              <option value="any">Dowolne</option>
              <option value="true">Tylko premium</option>
              <option value="false">Bez premium</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="roleFilter">Rola</label>
            <select
              id="roleFilter"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as any)}
              className="select"
            >
              <option value="any">Dowolna</option>
              <option value="admin">Admin</option>
              <option value="worker">Worker</option>
              <option value="user">User</option>
              <option value="guest">Guest</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="inactiveFilter">Ostatnia aktywność</label>
            <select
              id="inactiveFilter"
              value={inactiveGtDays}
              onChange={e => setInactiveGtDays(e.target.value as any)}
              className="select"
              title="Pokaż użytkowników aktywnych dawniej niż X dni temu (lub nigdy)"
            >
              <option value="any">Dowolnie</option>
              <option value="10">więcej niż 10 dni</option>
              <option value="30">więcej niż 30 dni</option>
              <option value="60">więcej niż 60 dni</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => {
              searchTermRef.current = query.trim();
              setPage(1);
            }}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Ładowanie…' : 'Szukaj'}
          </button>
        </div>
      </section>

      {/* Widoczność kolumn */}
      <div className="cols-control">
        <button
          type="button"
          className="btn btn-ghost cols-btn"
          onClick={() => setColsOpen(v => !v)}
          aria-expanded={colsOpen}
          title="Widoczność kolumn"
          ref={colsBtnRef}
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          <span>Widoczność</span>
        </button>

        {colsOpen && (
          <div
            className="cols-panel"
            role="dialog"
            aria-label="Wybór kolumn"
            ref={colsPanelRef}
          >
            {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map(k => (
              <label key={k} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={visibleCols[k]}
                  onChange={e =>
                    setVisibleCols(v => ({
                      ...v,
                      [k]: e.target.checked,
                    }))
                  }
                />
                {COLUMN_LABELS[k]}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Tabela */}
      <section aria-busy={loading} className="card">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }}></th>
                <th>ID</th>

                {visibleCols.email && <th>Email</th>}
                {visibleCols.role && <th>Rola</th>}
                {visibleCols.verified && <th>Zweryfikowany</th>}
                {visibleCols.legal && <th>Regulamin/PP</th>}
                {visibleCols.notifications && <th>Powiadomienia</th>}
                {visibleCols.premium && <th>Premium</th>}
                {visibleCols.expires && <th>Wygasa</th>}
                {visibleCols.last_active && <th>Ostatnia aktywność</th>}
                {visibleCols.created && <th>Utworzono</th>}
                {visibleCols.actions && <th>Akcje</th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map(u => {
                const sub: any = (u as any).subscription || {};
                const premiumState: any = (u as any).premium || {};

                // unified expiry: premium_expires_at > subscription.expires_at
                const premiumExpiresRaw =
                  premiumState.premium_expires_at ||
                  sub?.expires_at ||
                  null;

                const expires = premiumExpiresRaw
                  ? new Date(premiumExpiresRaw).toLocaleString()
                  : '—';

                const lastActive = u.last_active
                  ? new Date(u.last_active).toLocaleString()
                  : '—';
                const inactiveDays = u.last_active
                  ? Math.floor((Date.now() - new Date(u.last_active).getTime()) / 86400000)
                  : Infinity;
                const lastActiveClass =
                  inactiveDays > 60 ? 'text-danger' : inactiveDays > 30 ? 'text-warning' : '';

                const profilesAllowed = allowedProfilesNow(u);
                const profiles = profilesCache[u.id];
                const legal =
                  u.accepted_latest_legal != null
                    ? {
                      accepted: !!u.accepted_latest_legal,
                      accepted_at: u.legal_accepted_at || null,
                    }
                    : legalCache[u.id];

                const detailsColSpan = 2 + visibleCount; // strzałka + ID + widoczne kolumny

                return (
                  <React.Fragment key={u.id}>
                    <tr>
                      <td>
                        <button
                          className="btn-ghost small"
                          onClick={() => toggleExpand(u)}
                          aria-expanded={expandedId === u.id}
                        >
                          {expandedId === u.id ? '▾' : '▸'}
                        </button>
                      </td>
                      <td className="mono">{u.id}</td>

                      {visibleCols.email && <td>{u.email}</td>}

                      {visibleCols.role && (
                        <td>
                          <div className="role-cell">
                            <select
                              className="select role-select"
                              value={u.role}
                              onChange={e => requestRoleChange(u, e.target.value as any)}
                              disabled={!!roleChanging && roleChanging === u.id}
                              title="Zmień rolę użytkownika"
                            >
                              {['admin', 'worker', 'user', 'guest'].map(r => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                              {!['admin', 'worker', 'user', 'guest'].includes(u.role) && (
                                <option value={u.role}>{u.role}</option>
                              )}
                            </select>
                            {roleChanging === u.id && (
                              <span className="spinner small" aria-label="Zapisywanie…"></span>
                            )}
                          </div>
                        </td>
                      )}

                      {visibleCols.verified && (
                        <td>
                          <span
                            className={`badge ${u.is_verified ? 'badge-success' : 'badge-muted'
                              }`}
                          >
                            {u.is_verified ? 'TAK' : 'NIE'}
                          </span>
                        </td>
                      )}

                      {visibleCols.legal && (
                        <td>
                          {legal ? (
                            <span
                              className={`badge ${legal.accepted ? 'badge-success' : 'badge-danger'
                                }`}
                              title={legal.accepted_at || ''}
                            >
                              {legal.accepted ? 'Zaakceptowano' : 'Brak akceptacji'}
                            </span>
                          ) : (
                            <span className="badge badge-muted">—</span>
                          )}
                        </td>
                      )}

                      {visibleCols.notifications && (
                        <td>
                          <NotificationsCell
                            email={u.accepts_email_notifications}
                            local={u.accepts_local_notifications}
                          />
                        </td>
                      )}

                      {visibleCols.premium && (
                        <td>
                          {(() => {
                            const planCount = getProfilesCount(u);
                            const premiumState: any = (u as any).premium || {};
                            const source = premiumState.premium_source as
                              | 'manual'
                              | 'revenuecat'
                              | null;

                            return (
                              <span
                                className={`badge ${u.is_premium ? 'badge-success' : 'badge-muted'
                                  }`}
                              >
                                {u.is_premium ? (
                                  <>
                                    TAK
                                    {source && (
                                      <span
                                        className="chip chip-xs"
                                        style={{ marginLeft: 6 }}
                                        title={
                                          source === 'revenuecat'
                                            ? 'Premium z RevenueCat (subskrypcja / grant)'
                                            : 'Premium nadane ręcznie / kodem'
                                        }
                                      >
                                        {source === 'revenuecat' ? 'RC' : 'MANUAL'}
                                      </span>
                                    )}
                                    {planCount && <ProfilesChip count={planCount} />}
                                  </>
                                ) : (
                                  'NIE'
                                )}
                              </span>
                            );
                          })()}
                        </td>
                      )}

                      {visibleCols.expires && <td>{expires}</td>}
                      {visibleCols.last_active && (
                        <td className={lastActiveClass}>{lastActive}</td>
                      )}
                      {visibleCols.created && (
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      )}

                      {visibleCols.actions && (
                        <td className="actions">
                          <button
                            onClick={() => openConfirm(u, 'grant')}
                            className="btn btn-ghost"
                          >
                            1 m-c
                          </button>
                          <button
                            onClick={() => openConfirm(u, 'extend')}
                            className="btn btn-ghost"
                          >
                            + 1 m-c
                          </button>
                          <button
                            onClick={() => openConfirm(u, 'revoke')}
                            className="btn btn-danger"
                          >
                            Cofnij
                          </button>
                        </td>
                      )}
                    </tr>

                    {/* Wiersz szczegółów */}
                    {expandedId === u.id && (
                      <tr className="details-row">
                        <td colSpan={detailsColSpan}>
                          {loadingDetails === u.id && (
                            <div className="muted">Ładowanie szczegółów…</div>
                          )}

                          {/* PROFILKI */}
                          {profiles && (
                            <div className="profiles-block">
                              <div className="profiles-summary">
                                <b>Profile użytkownika</b>{' '}
                                <span className="muted">
                                  (dozwolone teraz: {profilesAllowed})
                                </span>
                              </div>

                              <ul className="profiles-list">
                                {profiles.map((p, idx) => {
                                  const active = idx < profilesAllowed;
                                  return (
                                    <li
                                      key={p.id}
                                      className={`profile-item ${active ? '' : 'inactive'
                                        }`}
                                    >
                                      <div className="profile-line">
                                        <span className="mono">#{p.id}</span>{' '}
                                        <b>{p.name}</b> ({p.age})
                                        {p.is_main && (
                                          <span className="chip">MAIN</span>
                                        )}
                                        {!active && (
                                          <span
                                            className="chip chip-danger"
                                            title="Poza limitem planu / po wygaśnięciu premium"
                                          >
                                            NIEAKTYWNY
                                          </span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                                {profiles.length === 0 && (
                                  <li className="muted">Brak profili</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* PRAWO */}
                          {legal && (
                            <div className="legal-block">
                              <div>
                                <b>Regulamin/PP:</b>{' '}
                                {legal.accepted
                                  ? 'Zaakceptowano'
                                  : 'Brak akceptacji'}
                              </div>
                              {legal.accepted_at && (
                                <div className="muted">
                                  Data akceptacji:{' '}
                                  {new Date(
                                    legal.accepted_at
                                  ).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}

                          {/* DANE NAUKI – pod regulaminem */}
                          {profiles && profiles.length > 0 && (
                            <div className="profiles-block">
                              <div className="profiles-summary" style={{ marginTop: 8 }}>
                                <b>Dane profilu</b>
                              </div>
                              <ul className="profiles-list">
                                {profiles.map(p => {
                                  const levelLabel =
                                    p.learning_level != null
                                      ? LEVEL_LABELS[p.learning_level] || `Poziom ${p.learning_level}`
                                      : null;
                                  const mainGoalLabel =
                                    p.learning_goal != null
                                      ? (LEARNING_GOAL_LABELS[p.learning_goal] || p.learning_goal)
                                      : null;
                                  const extraGoalsText = formatGoals(p.learning_goals);
                                  const minutesText =
                                    p.daily_minutes != null
                                      ? `${p.daily_minutes} min/dzień`
                                      : 'brak';
                                  const interestsText = formatInterests(p.interest_category_ids);

                                  const hasAnyMeta =
                                    levelLabel ||
                                    mainGoalLabel ||
                                    (p.learning_goals && p.learning_goals.length > 0) ||
                                    p.daily_minutes != null ||
                                    (p.interest_category_ids &&
                                      p.interest_category_ids.length > 0);

                                  return (
                                    <li key={p.id} className="profile-item">
                                      <div className="profile-line">
                                        <b>{p.name}</b>{' '}
                                        <span className="mono">#{p.id}</span>
                                      </div>
                                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                        {hasAnyMeta ? (
                                          <>
                                            Poziom:{' '}
                                            <span>
                                              {levelLabel || 'brak'}
                                            </span>{' '}
                                            · Cel główny:{' '}
                                            <span>
                                              {mainGoalLabel || 'brak'}
                                            </span>{' '}
                                            · Cele dodatkowe:{' '}
                                            <span>
                                              {extraGoalsText}
                                            </span>{' '}
                                            · Minuty:{' '}
                                            <span>
                                              {minutesText}
                                            </span>{' '}
                                            · Zainteresowania:{' '}
                                            <span>
                                              {interestsText}
                                            </span>
                                          </>
                                        ) : (
                                          <>brak</>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={2 + visibleCount} className="empty">
                    Brak wyników
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginacja */}
        <div className="pagination">
          <button
            className="btn btn-ghost"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            ◀
          </button>
          <span>
            Strona {page} / {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            ▶
          </button>
        </div>
      </section>

      {error && <div className="alert error">{error}</div>}

      {/* Modal – premium */}
      {confirmOpen && pendingAction && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div className="modal">
            <h3 id="confirm-title" className="modal-title">
              Potwierdź akcję
            </h3>

            {pendingAction.action !== 'revoke' && (
              <>
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Rodzaj premium</label>
                  <select
                    className="select"
                    value={pendingAction.plan_type || 'BASIC'}
                    onChange={e =>
                      setPendingAction(prev =>
                        prev
                          ? {
                            ...prev,
                            plan_type: e.target.value as PlanType,
                          }
                          : prev
                      )
                    }
                  >
                    <option value="BASIC">BASIC (1 profil)</option>
                    <option value="STANDARD">STANDARD (2 profile)</option>
                    <option value="FAMILY">FAMILY (4 profile)</option>
                  </select>
                </div>

                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Ile miesięcy</label>
                  <input
                    type="number"
                    min={1}
                    max={36}
                    className="input"
                    value={months}
                    onChange={e =>
                      setMonths(Math.max(1, Number(e.target.value) || 1))
                    }
                  />
                </div>
              </>
            )}

            <p className="modal-text">
              Czy na pewno chcesz{' '}
              <b>
                {pendingAction.action === 'revoke'
                  ? 'COFNĄĆ'
                  : pendingAction.action === 'grant'
                    ? 'PRZYZNAĆ'
                    : 'PRZEDŁUŻYĆ'}
              </b>{' '}
              premium{' '}
              {pendingAction.action === 'revoke'
                ? ''
                : `na ${months} m-c`}{' '}
              {pendingAction.action !== 'revoke' && pendingAction.plan_type ? (
                <>
                  {' '}
                  (plan: <b>{pendingAction.plan_type}</b>, profile:{' '}
                  <b>{PLAN_PROFILES[pendingAction.plan_type]}</b>)
                </>
              ) : null}
              ?
            </p>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={closeConfirm}>
                Anuluj
              </button>
              <button
                className={`btn ${pendingAction.action === 'revoke'
                    ? 'btn-danger'
                    : 'btn-primary'
                  }`}
                onClick={confirmAction}
              >
                Potwierdź
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal – zmiana roli */}
      {roleConfirmOpen && pendingRole && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-role-title"
        >
          <div className="modal">
            <h3 id="confirm-role-title" className="modal-title">
              Potwierdź zmianę roli
            </h3>
            <p className="modal-text">
              Czy na pewno chcesz zmienić rolę użytkownika{' '}
              <b>{pendingRole.email}</b>
              <br />
              z <b>{pendingRole.prevRole}</b> na{' '}
              <b>{pendingRole.nextRole}</b>?
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setRoleConfirmOpen(false);
                  setPendingRole(null);
                }}
              >
                Anuluj
              </button>
              <button className="btn btn-primary" onClick={confirmRoleChange}>
                Potwierdź
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}